import { useEffect, useMemo } from 'react'
import { fetchPropertyRates } from 'actions/PropertyActions'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK } from 'constants/offer'
import usePropertyOfferMapping from 'hooks/Offers/usePropertyOfferMapping'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { EmptyArray } from 'lib/array/arrayUtils'
import getPropertyRatesReduxKeys from 'lib/offer/getPropertyRatesReduxKeys'
import { isUnavailableRoomRates } from 'lib/offer/propertyTypes'
import { isDisplayablePrice } from 'lib/offer/pricingVisibility'
import { getCanViewLuxPlusBenefits, getIsLuxPlusLPPEnabled } from 'luxPlus/selectors/featureToggle'

export function getCheapestSimilarPropertyRate(rooms: Array<App.RawPropertyRoomRates>, canViewMemberPrice: boolean, lppEnabled = true): App.RawPropertyRate | undefined {
  const rates = rooms.flatMap(room => isUnavailableRoomRates(room) ? [] : room.lists.hotelOnly.flatMap(({ id }) => room.rates[id] ?? []))
  const displayPrice = (rate: App.RawPropertyRate) => canViewMemberPrice && (rate.service.type !== 'bedbank' || lppEnabled) && isDisplayablePrice(rate.luxPlusTotals?.displayPrice)
    ? Math.min(rate.totals.displayPrice, rate.luxPlusTotals.displayPrice)
    : rate.totals.displayPrice
  return rates.filter(rate => !rate.isFlightBundle && isDisplayablePrice(rate.totals.displayPrice))
    .reduce<App.RawPropertyRate | undefined>((cheapest, rate) => !cheapest || displayPrice(rate) < displayPrice(cheapest) ? rate : cheapest, undefined)
}

export function useSimilarPropertyRate(offerId: string | undefined, filters: App.OfferListFilters, offerType: typeof OFFER_TYPE_ALWAYS_ON | typeof OFFER_TYPE_BED_BANK = OFFER_TYPE_ALWAYS_ON) {
  const { checkIn, checkOut, rooms = EmptyArray } = filters
  const dispatch = useAppDispatch()
  const lppEnabled = useAppSelector(getIsLuxPlusLPPEnabled)
  const canViewMemberPrice = useAppSelector(getCanViewLuxPlusBenefits)
  const [mapping, mappingLoading, mappingError] = usePropertyOfferMapping(offerType, offerId, { disabled: !offerId })
  const hasMapping = !!mapping?.propertyId && (offerType !== OFFER_TYPE_BED_BANK || mapping.bedbankPropertyIds.includes(offerId ?? ''))
  const params = useMemo(() => ({
    propertyId: hasMapping ? mapping.propertyId : undefined,
    checkIn,
    checkOut,
    occupancies: rooms,
    utmContent: undefined,
    roomTypeId: undefined,
    roomRateId: undefined,
  }), [hasMapping, mapping?.propertyId, checkIn, checkOut, rooms])
  const key = getPropertyRatesReduxKeys(params)?.combined
  const rates = useAppSelector(state => state.offer.propertyRates[key ?? ''])
  useEffect(() => {
    if (params.propertyId && params.checkIn && params.checkOut) {
      dispatch(fetchPropertyRates(params.propertyId, { ...params, checkIn: params.checkIn, checkOut: params.checkOut }))
    }
  }, [dispatch, params])
  const cheapest = useMemo(() => getCheapestSimilarPropertyRate(rates?.rooms ?? EmptyArray, canViewMemberPrice, lppEnabled), [rates?.rooms, canViewMemberPrice, lppEnabled])
  return { hasMapping, rate: cheapest, loading: mappingLoading || !!(key && (!rates || rates.fetching)), error: mappingError || rates?.error, lppEnabled }
}
